"use client";

import React, { createContext, useContext, useState, useEffect, useRef, useCallback } from 'react';
import { Client, IMessage, StompSubscription } from '@stomp/stompjs';
import * as gtag from '@/lib/gtag';

const BACKEND_URL = process.env.NEXT_PUBLIC_BACKEND_URL || '/api';
const WS_URL = process.env.NEXT_PUBLIC_WS_URL || '/api/ws';

const generateId = () => Math.random().toString(36).substr(2, 9);

export type GameStatus = 'LOBBY' | 'WAITING' | 'PREPARING' | 'COUNTDOWN' | 'RECONNECTING' | 'PLAYING' | 'GAMEOVER' | 'MATCHING' | 'MATCH_DECLINED' | 'OPPONENT_LEFT';

const ERROR_MESSAGES: Record<string, string> = {
  '404_0WNF': 'Kjo fjalë nuk ekziston në fjalorin tonë.',
  '404_0WMNF': 'Kuptimi i kësaj fjale nuk u gjet.',
  '404_0GNF': 'Dhoma nuk u gjet! Kontrolloni kodin.',
  '400_0NAW': 'Fjala përmban shkronja që nuk janë në alfabetin shqip.',
  '400_0AWS': 'Fjala është përdorur tashmë.',
  '400_0IWS': 'Fjala e dërguar nuk është e vlefshme.',
  '400_0ITA': 'Nuk është radha juaj!',
  '429_RATE_LIMIT': 'Keni dërguar shumë kërkesa. Ju lutem prisni pak.',
  'API_500_1': 'Loja ka probleme teknike, provoni përsëri më vonë.',
  'default': 'Pati një problem me kërkesën tuaj.'
};

export interface PlayerScore {
  points: number;
  totalWords: number;
}

export interface Player {
  playerId: string;
  name: string;
  isAi: boolean;
  score: PlayerScore;
  connectionStatus: 'CONNECTED' | 'DISCONNECTED';
  readiness: 'WAITING' | 'READY' | 'NOT_READY' | 'REFUSED';
}

export interface GameState {
  gameId: string;
  mode: 'SOLO_AI' | 'PVP_MATCHMAKING' | 'PVP_PRIVATE_ROOM';
  status: string;
  player1: Player;
  player2?: Player;
  currentTurnPlayerId: string;
  playedWords: string[];
  lastPlayedWord?: string;
  requiredStartLetter?: string;
  winnerId?: string;
  turnDeadLine?: string;
  winningReason?: 'NORMAL_WIN' | 'TIME_EXPIRED' | 'OPPONENT_ABANDONED' | 'RESIGNED';
}

export interface GameHistoryItem {
  word: string;
  originalWord?: string;
  playerId: string;
  score: number;
  insight?: string;
}

interface GameContextType {
  isConnected: boolean;
  status: GameStatus;
  gameState: GameState | null;
  history: GameHistoryItem[];
  playerName: string;
  playerId: string;
  isOpponentThinking: boolean;
  joinAiGame: (name: string, autoReady?: boolean) => Promise<void>;
  joinQueue: (name: string) => Promise<void>;
  createCustomGame: (name: string, autoReady?: boolean) => Promise<void>;
  joinCustomGame: (gameId: string, name: string, autoReady?: boolean) => Promise<any>;
  cancelPrivateGame: () => Promise<void>;
  triggerReady: () => void;
  refuseMatch: () => void;
  requestRematch: () => void;
  acceptRematch: () => void;
  declineRematch: () => void;
  makeMove: (word: string) => void;
  resetGame: () => void;
  setStatus: (status: GameStatus) => void;
  message: { text: string; type: 'error' | 'success' | 'info' } | null;
  setMessage: (message: { text: string; type: 'error' | 'success' | 'info' } | null) => void;
  isSystemHealthy: boolean | null;
  serverTimeOffset: number;
  refusedByPlayerId: string | null;
  isOpponentDisconnected: boolean;
  rematchRequestBy: string | null;
  breakReminderEnabled: boolean;
  setBreakReminderEnabled: (enabled: boolean) => void;
  isBreakReminderVisible: boolean;
  dismissBreakReminder: () => void;
  customKeyboardEnabled: boolean;
  setCustomKeyboardEnabled: (enabled: boolean) => void;
  BACKEND_URL: string;
  fetchWithId: (url: string, options?: RequestInit) => Promise<Response>;
  gameCount: number;
  incrementGameCount: () => void;
  isRatingModalVisible: boolean;
  setIsRatingModalVisible: (visible: boolean) => void;
}

const GameContext = createContext<GameContextType | undefined>(undefined);

export const useGame = () => {
  const context = useContext(GameContext);
  if (!context) {
    throw new Error('useGame must be used within a GameProvider');
  }
  return context;
};

interface PlayedWord {
  word: string;
  playerId: string;
  points: number;
  insight: string;
  sortOrder: number;
}

interface BackendSession {
  sessionId: string;
  mode: 'SOLO_AI' | 'PVP_MATCHMAKING' | 'PVP_PRIVATE_ROOM';
  status: string;
  player1: Player;
  player2?: Player;
  currentTurnPlayerId: string;
  playedWords?: PlayedWord[];
  lastPlayedWord?: string;
  requiredStartLetter?: string;
  winnerId?: string;
  turnDeadLine?: string;
  winningReason?: 'NORMAL_WIN' | 'TIME_EXPIRED' | 'OPPONENT_ABANDONED' | 'RESIGNED';
}

interface GameEvent {
  type?: string;
  eventType?: string;
  getType?: () => string;
  session?: BackendSession;
  sessionId?: string;
  player?: Player;
  playerId?: string;
  winnerId?: string;
  winningReason?: 'NORMAL_WIN' | 'TIME_EXPIRED' | 'OPPONENT_ABANDONED' | 'RESIGNED';
  isThinking?: boolean;
  evaluation?: {
    isValid: boolean;
    properWord?: string;
    submittedWord: string;
    pointsAwarded: number;
    insightMessage?: string;
  };
  nextTurnPlayerId?: string;
  requesterId?: string;
  turnDeadLine?: string;
  message?: string;
  code?: string;
  error?: string;
}

export const GameProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const stompClientRef = useRef<Client | null>(null);
  const [isConnected, setIsConnected] = useState(false);
  const [status, _setStatus] = useState<GameStatus>('LOBBY');
  const statusRef = useRef<GameStatus>('LOBBY');
  const setStatus = useCallback((s: GameStatus | ((prev: GameStatus) => GameStatus)) => {
    if (typeof s === 'function') {
      _setStatus(prev => {
        const next = s(prev);
        if (next !== prev) {
          if (next === 'PLAYING') {
            gtag.event({ action: 'game_start', category: 'game', label: gameStateRef.current?.mode || 'unknown' });
          } else if (next === 'GAMEOVER') {
            gtag.event({ action: 'game_over', category: 'game', label: gameStateRef.current?.mode || 'unknown' });
          }
        }
        statusRef.current = next;
        return next;
      });
    } else {
      if (s !== statusRef.current) {
        if (s === 'PLAYING') {
          gtag.event({ action: 'game_start', category: 'game', label: gameStateRef.current?.mode || 'unknown' });
        } else if (s === 'GAMEOVER') {
          gtag.event({ action: 'game_over', category: 'game', label: gameStateRef.current?.mode || 'unknown' });
        }
      }
      statusRef.current = s;
      _setStatus(s);
    }
  }, []);
  const [gameState, _setGameState] = useState<GameState | null>(null);
  const gameStateRef = useRef<GameState | null>(null);
  const setGameState = useCallback((state: GameState | null | ((prev: GameState | null) => GameState | null)) => {
    if (typeof state === 'function') {
      _setGameState(prev => {
        const next = state(prev);
        gameStateRef.current = next;
        return next;
      });
    } else {
      gameStateRef.current = state;
      _setGameState(state);
    }
  }, []);
  const [history, setHistory] = useState<GameHistoryItem[]>([]);
  const [playerName, setPlayerName] = useState('');
  const [playerId, setPlayerId] = useState<string>('');
  
  const messageRef = useRef<{ text: string; type: 'error' | 'success' | 'info' } | null>(null);
  const [message, _setMessage] = useState<{ text: string; type: 'error' | 'success' | 'info' } | null>(null);
  
  const [isSystemHealthy, setIsSystemHealthy] = useState<boolean | null>(null);
  const [isOpponentThinking, setIsOpponentThinking] = useState(false);
  const [lastAttemptedWord, setLastAttemptedWord] = useState('');
  const [serverTimeOffset, setServerTimeOffset] = useState(0);
  const [refusedByPlayerId, setRefusedByPlayerId] = useState<string | null>(null);
  const [isOpponentDisconnected, setIsOpponentDisconnected] = useState(false);
  const [rematchRequestBy, setRematchRequestBy] = useState<string | null>(null);

  const [breakReminderEnabled, _setBreakReminderEnabled] = useState<boolean>(() => {
    if (typeof window !== 'undefined') {
      const stored = localStorage.getItem('fjalafundit_break_reminder');
      return stored === null ? true : stored === 'true';
    }
    return true;
  });
  const [isBreakReminderVisible, setIsBreakReminderVisible] = useState(false);
  const sessionStartTimeRef = useRef<number>(Date.now());
  const hasShownBreakReminderRef = useRef(false);

  const setBreakReminderEnabled = (enabled: boolean) => {
    _setBreakReminderEnabled(enabled);
    localStorage.setItem('fjalafundit_break_reminder', String(enabled));
  };

  const dismissBreakReminder = () => {
    setIsBreakReminderVisible(false);
    hasShownBreakReminderRef.current = true;
  };

  const [customKeyboardEnabled, _setCustomKeyboardEnabled] = useState<boolean>(() => {
    if (typeof window !== 'undefined') {
      const stored = localStorage.getItem('fjalafundit_custom_keyboard');
      return stored === null ? true : stored === 'true';
    }
    return true;
  });

  const [gameCount, setGameCount] = useState<number>(() => {
    if (typeof window !== 'undefined') {
      const stored = localStorage.getItem('fjalafundit_game_count');
      return stored ? parseInt(stored, 10) : 0;
    }
    return 0;
  });

  const incrementGameCount = useCallback(() => {
    setGameCount(prev => {
      const next = prev + 1;
      localStorage.setItem('fjalafundit_game_count', next.toString());
      return next;
    });
  }, []);

  const [isRatingModalVisible, setIsRatingModalVisible] = useState(false);

  const setCustomKeyboardEnabled = (enabled: boolean) => {
    _setCustomKeyboardEnabled(enabled);
    if (typeof window !== 'undefined') {
      localStorage.setItem('fjalafundit_custom_keyboard', String(enabled));
    }
  };

  // Break Reminder Logic: Check every minute if 1h has passed
  useEffect(() => {
    if (!breakReminderEnabled || hasShownBreakReminderRef.current) return;

    const interval = setInterval(() => {
      const elapsed = Date.now() - sessionStartTimeRef.current;
      const ONE_HOUR = 60 * 60 * 1000;

      if (elapsed >= ONE_HOUR && statusRef.current === 'LOBBY') {
        setIsBreakReminderVisible(true);
        // We don't clear interval yet because user might disable it while modal is open, 
        // but hasShownBreakReminderRef will prevent re-triggers.
      }
    }, 60000); // Check every minute

    return () => clearInterval(interval);
  }, [breakReminderEnabled]);

  const gameSubscriptionRef = useRef<StompSubscription | null>(null);
  const handleGameEventRef = useRef<(event: GameEvent) => void>(() => {});
  const skipCountdownRef = useRef(false);

  const setMessage = useCallback((msg: { text: string; type: 'error' | 'success' | 'info' } | null) => {
    messageRef.current = msg;
    _setMessage(msg);
  }, []);

  useEffect(() => {
    const checkHealth = async () => {
      try {
        const start = Date.now();
        const res = await fetch(`${BACKEND_URL}/health/status`);
        const end = Date.now();

        const response = await res.json();
        const healthy = response.success && response.data?.healthy === true;
        setIsSystemHealthy(healthy);

        if (healthy && response.data.serverTime) {
          const serverTime = response.data.serverTime;
          const latency = (end - start) / 2;
          // Add a 500ms buffer to the offset. 
          // This ensures the frontend timer is always ~500ms ahead of the backend,
          // so it never feels like the game ended "while there was still time left"
          const safetyBuffer = 500; 
          setServerTimeOffset(serverTime - (end - latency) + safetyBuffer);
        }
      } catch (err) {
        console.error('Health check failed:', err);
        setIsSystemHealthy(false);
      }
    };
    checkHealth();
  }, []);

  const mapBackendSessionToState = useCallback((session: BackendSession): GameState => {
    return {
      gameId: session.sessionId,
      mode: session.mode,
      status: session.status,
      player1: session.player1,
      player2: session.player2,
      currentTurnPlayerId: session.currentTurnPlayerId,
      playedWords: session.playedWords?.map(pw => pw.word) || [],
      lastPlayedWord: session.lastPlayedWord,
      requiredStartLetter: session.requiredStartLetter,
      winnerId: session.winnerId,
      turnDeadLine: session.turnDeadLine,
      winningReason: session.winningReason
    };
  }, []);

  const subscribeToGame = useCallback((sessionId: string, clientOverride?: Client) => {
    const client = clientOverride || stompClientRef.current;
    if (client && client.connected) {
      // Check if we need to reconnect because headers are outdated
      const currentHeaders = client.connectHeaders || {};
      if (currentHeaders.sessionId !== sessionId) {
        if (process.env.NODE_ENV === 'development') console.log('Session ID changed, reconnecting WebSocket to update headers...');
        client.connectHeaders = {
          ...currentHeaders,
          sessionId: sessionId
        };
        
        // StompJS doesn't support live header updates, so we must cycle the connection
        // We'll use a small timeout to let the deactivate finish before reactivate
        client.deactivate().then(() => {
          client.activate();
        });
        return; // The onConnect will call subscribeToGame again via restoreSession or flow
      }

      if (gameSubscriptionRef.current) {
        gameSubscriptionRef.current.unsubscribe();
      }
      gameSubscriptionRef.current = client.subscribe(`/topic/game/${sessionId}`, (msg: IMessage) => {
        handleGameEventRef.current(JSON.parse(msg.body));
      });
      if (process.env.NODE_ENV === 'development') console.log(`Subscribed to game: ${sessionId}`);
    }
  }, []);

  const handleGameEvent = useCallback((event: GameEvent) => {
    if (process.env.NODE_ENV === 'development') console.log('Incoming Game Event:', event);
    
    // Robust type extraction: check common properties if 'type' is missing
    let type = event.type || event.eventType || (event as any).getType?.();
    
    if (!type && event.winningReason) type = 'GAME_OVER';
    if (!type && event.evaluation) type = 'WORD_PLAYED';
    if (!type && event.session) type = 'STATE_UPDATE';
    if (!type && event.isThinking !== undefined) type = 'OPPONENT_THINKING';
    
    // Improved inference for player status events
    if (!type && event.playerId && !event.session && !event.player && !event.winnerId) {
       // Only default to DISCONNECTED if we are SURE it's not something else
       // but since we added PLAYER_LEFT, we should be careful.
       // If the backend is working correctly, 'type' should be present.
       type = 'PLAYER_DISCONNECTED';
    }

    switch (type) {
      case 'MATCH_FOUND':
        if (event.session) {
          setRefusedByPlayerId(null);
          setMessage(null);
          const state = mapBackendSessionToState(event.session);
          localStorage.setItem('fjalafundit_session_id', state.gameId);
          setGameState(state);
          setStatus('MATCHING');
          subscribeToGame(state.gameId);
        }
        break;

      case 'PLAYER_JOINED':
        setRefusedByPlayerId(null);
        setMessage(null);
        if (event.session) {
          const state = mapBackendSessionToState(event.session);
          setGameState(state);
          setStatus('MATCHING');
          subscribeToGame(state.gameId);
        } else if (event.player) {
          setGameState(prev => {
            if (!prev) return prev;
            return { ...prev, player2: event.player };
          });
          setStatus('MATCHING');
        }
        break;

      case 'MATCH_DECLINED':
      case 'REMATCH_DECLINED':
        if (event.playerId) {
          setRefusedByPlayerId(event.playerId);
          setStatus('MATCH_DECLINED');
          setRematchRequestBy(null);
          
          if (event.session) {
            setGameState(mapBackendSessionToState(event.session));
          } else {
            // Force update readiness in local state if no session provided
            setGameState(prev => {
              if (!prev) return null;
              const newPlayer1 = { ...prev.player1 };
              const newPlayer2 = prev.player2 ? { ...prev.player2 } : undefined;
              
              // Set the refuser to NOT_READY to ensure the "REFUZOI" stamp wins over "GATI"
              if (event.playerId === newPlayer1.playerId) newPlayer1.readiness = 'NOT_READY';
              else if (newPlayer2 && event.playerId === newPlayer2.playerId) newPlayer2.readiness = 'NOT_READY';
              
              return { ...prev, player1: newPlayer1, player2: newPlayer2 };
            });
          }
          
          // Automatically return to lobby after 3.5 seconds
          setTimeout(() => {
            if (gameStateRef.current?.mode === 'PVP_PRIVATE_ROOM') {
              resetGame();
            } else {
              const currentName = localStorage.getItem('fjalafundit_last_name') || playerName;
              joinQueue(currentName);
            }
          }, 3500);
        }
        break;

      case 'PLAYER_READY':
        setGameState(prev => {
          if (!prev || !event.playerId) return prev;
          const newPlayer1 = { ...prev.player1 };
          const newPlayer2 = prev.player2 ? { ...prev.player2 } : undefined;

          const pId = event.playerId;
          if (pId === newPlayer1.playerId) newPlayer1.readiness = 'READY';
          else if (newPlayer2 && pId === newPlayer2.playerId) newPlayer2.readiness = 'READY';

          return { ...prev, player1: newPlayer1, player2: newPlayer2 };
        });
        break;

      case 'REMATCH_REQUESTED':
        if (event.playerId || event.requesterId) {
          setRematchRequestBy(event.playerId || event.requesterId || null);
        }
        if (event.session) {
          setGameState(mapBackendSessionToState(event.session));
        }
        break;

      case 'REMATCH_DECLINED':
        if (event.playerId) {
          setRefusedByPlayerId(event.playerId);
          setStatus('MATCH_DECLINED');
          // Automatically return to lobby after 3.5 seconds
          setTimeout(() => {
            resetGame();
          }, 3500);
        }
        break;

      case 'PLAYER_LEFT':
        if (event.playerId !== playerId) {
          setRematchRequestBy(null);
          setRefusedByPlayerId(event.playerId || null);
          setMessage({ text: 'Kundërshtari u largua nga loja.', type: 'info' });
          
          // If we were in the game over screen, transition to a specific view
          // showing that the opponent has left, before returning to lobby.
          if (statusRef.current === 'GAMEOVER' && gameStateRef.current?.mode === 'PVP_PRIVATE_ROOM') {
            setStatus('OPPONENT_LEFT');
            // Automatically return to lobby after 2 seconds to let them read the message
            setTimeout(() => {
              resetGame();
            }, 3000);
          }
        }
        break;

      case 'STATE_UPDATE':
        if (event.session) {
          const state = mapBackendSessionToState(event.session);
          setGameState(state);

          const opp = state.player1.playerId === playerId ? state.player2 : state.player1;
          const isGameOver = state.status === 'FINISHED' || state.status === 'ABANDONED' || state.status === 'TIMEOUT_TERMINATED';
          
          if (opp?.connectionStatus === 'DISCONNECTED' && !isGameOver) {
            setIsOpponentDisconnected(true);
          } else {
            setIsOpponentDisconnected(false);
          }
          
          if (state.status === 'WAITING_FOR_OPPONENT') {
            setStatus('WAITING');
          } else if (state.status === 'WAITING_FOR_READINESS') {
            setRefusedByPlayerId(null);
            setStatus(current => {
              if (skipCountdownRef.current || current === 'PLAYING' || current === 'COUNTDOWN') return current;
              return state.mode === 'SOLO_AI' ? 'PREPARING' : 'MATCHING';
            });
          } else if (state.status === 'WAITING_FIRST_WORD' || state.status === 'IN_PROGRESS') {
            setRematchRequestBy(null);
            setRefusedByPlayerId(null);
            
            // If we are currently matching, wait a bit so players see the "GATI" badges
            if (statusRef.current === 'MATCHING' && !skipCountdownRef.current) {
              if (process.env.NODE_ENV === 'development') console.log('Match confirmed, waiting 2s before countdown...');
              setTimeout(() => {
                setStatus('COUNTDOWN');
              }, 1200);
              return;
            }

            setStatus(current => {
              // Priority 1: Respect skip countdown flag
              if (skipCountdownRef.current) {
                // Keep the flag true until we are sure we've settled in PLAYING
                // It will be cleared once we are in the PLAYING status or here.
                skipCountdownRef.current = false;
                setMessage(null);
                return 'PLAYING';
              }

              // Priority 2: Instant start for PvP rematch OR Solo AI rematch (coming from GAMEOVER)
              if (current === 'GAMEOVER') {
                setHistory([]);
                setMessage(null);
                return 'PLAYING';
              }
              
              // Priority 3: If we already jumped to PLAYING (e.g. via joinAiGame autoReady), stay there
              if (current === 'PLAYING' || (state.mode === 'SOLO_AI' && current !== 'PREPARING' && current !== 'LOBBY')) {
                return 'PLAYING';
              }

              if (current === 'COUNTDOWN') return current;
              
              // Clear any 'Waiting' or 'Matching' info messages when countdown starts
              setMessage(null);

              // Final check for Solo AI fresh start
              if (state.mode === 'SOLO_AI' && (current === 'LOBBY' || current === 'PREPARING')) {
                return 'COUNTDOWN';
              }
              
              return 'COUNTDOWN';
            });
          } else if (state.status === 'FINISHED' || state.status === 'ABANDONED' || state.status === 'TIMEOUT_TERMINATED') {
            setStatus('GAMEOVER');
            if (!messageRef.current) {
              const isWinner = state.winnerId === playerId;
              setMessage({ 
                text: isWinner ? 'Urime! Ju jeni fituesi i kësaj loje.' : 'Loja përfundoi. Fitues është kundërshtari.',
                type: isWinner ? 'success' : 'error' 
              });
            }
          }
        }
        break;
      
      case 'OPPONENT_THINKING':
        setIsOpponentThinking(!!event.isThinking);
        break;

      case 'PLAYER_DISCONNECTED':
        if (event.playerId !== playerId) {
          setIsOpponentDisconnected(true);
          setMessage({ text: 'Kundërshtari u shkëput! Duke pritur te rikthehet...', type: 'info' });
          setGameState(prev => {
            if (!prev) return null;
            const newPlayer1 = { ...prev.player1 };
            const newPlayer2 = prev.player2 ? { ...prev.player2 } : undefined;
            if (event.playerId === newPlayer1.playerId) newPlayer1.connectionStatus = 'DISCONNECTED';
            else if (newPlayer2 && event.playerId === newPlayer2.playerId) newPlayer2.connectionStatus = 'DISCONNECTED';
            return { ...prev, player1: newPlayer1, player2: newPlayer2 };
          });
        }
        break;

      case 'PLAYER_RECONNECTED':
        if (event.playerId !== playerId) {
          // Opponent came back
          setIsOpponentDisconnected(false);
          setGameState(prev => {
            if (!prev) return null;
            const newPlayer1 = { ...prev.player1 };
            const newPlayer2 = prev.player2 ? { ...prev.player2 } : undefined;
            if (event.playerId === newPlayer1.playerId) newPlayer1.connectionStatus = 'CONNECTED';
            else if (newPlayer2 && event.playerId === newPlayer2.playerId) newPlayer2.connectionStatus = 'CONNECTED';

            if (prev.status === 'IN_PROGRESS' || prev.status === 'WAITING_FIRST_WORD') setStatus('PLAYING');
            else if (prev.status === 'WAITING_FOR_READINESS') setStatus(prev.mode === 'SOLO_AI' ? 'PREPARING' : 'MATCHING');

            return { ...prev, player1: newPlayer1, player2: newPlayer2 };
          });
        } else {
          // I came back
          setGameState(prev => {
            if (!prev) return null;
            const bStatus = prev.status;
            if (bStatus === 'IN_PROGRESS' || bStatus === 'WAITING_FIRST_WORD') setStatus('PLAYING');
            else if (bStatus === 'WAITING_FOR_READINESS') setStatus(prev.mode === 'SOLO_AI' ? 'PREPARING' : 'MATCHING');
            else if (bStatus === 'WAITING_FOR_OPPONENT') setStatus('WAITING');
            else if (bStatus === 'FINISHED' || bStatus === 'ABANDONED' || bStatus === 'TIMEOUT_TERMINATED') setStatus('GAMEOVER');
            return prev;
          });
        }
        break;

      case 'WORD_PLAYED': {
        setIsOpponentThinking(false);
        const evaluation = event.evaluation;
        if (evaluation) {
          if (evaluation.isValid) {
            gtag.event({
              action: 'word_played',
              category: 'game',
              label: event.playerId === playerId ? 'player' : 'opponent',
              value: evaluation.pointsAwarded
            });
          }
          const lastWord = evaluation.properWord || evaluation.submittedWord;

          setGameState(prev => {
            if (!prev) return null;

            // SECURITY: Only process words if the sessionId matches the current game
            if (event.sessionId && event.sessionId !== prev.gameId) {
              console.warn("Received word for different session:", event.sessionId);
              return prev;
            }

            const isP1 = event.playerId === prev.player1.playerId;
            const newPlayer1 = {...prev.player1};
            const newPlayer2 = prev.player2 ? {...prev.player2} : undefined;

            const points = evaluation.pointsAwarded;

            if (evaluation.isValid) {
              if (isP1) {
                newPlayer1.score = {
                  points: newPlayer1.score.points + points,
                  totalWords: newPlayer1.score.totalWords + 1
                };
              } else if (newPlayer2) {
                newPlayer2.score = {
                  points: newPlayer2.score.points + points,
                  totalWords: newPlayer2.score.totalWords + 1
                };
              }

              // Only update history if it's the current session
              if (!event.sessionId || event.sessionId === prev.gameId) {
                setHistory(h => {
                  if (h.length > 0 && h[0].word === lastWord && h[0].playerId === event.playerId) {
                    return h;
                  }
                  return [{
                    word: lastWord,
                    originalWord: evaluation.submittedWord !== lastWord ? evaluation.submittedWord : undefined,
                    playerId: event.playerId || '',
                    score: points,
                    insight: evaluation.insightMessage
                  }, ...h];
                });
              }
            }

            return {
              ...prev,
              player1: newPlayer1,
              player2: newPlayer2,
              currentTurnPlayerId: event.nextTurnPlayerId || prev.currentTurnPlayerId,
              lastPlayedWord: evaluation.isValid ? lastWord : prev.lastPlayedWord,
              playedWords: evaluation.isValid ? [...(prev.playedWords || []), lastWord] : (prev.playedWords || []),
              turnDeadLine: event.turnDeadLine || prev.turnDeadLine,
              winningReason: event.winningReason || prev.winningReason
            };
          });
        }
        if (evaluation?.isValid) setMessage(null);
        break;
      }

      case 'GAME_OVER':
      case 'TURN_TIMEOUT':
      case 'PLAYER_RESIGNED':
      case 'TIMEOUT_TERMINATED': {
        if (process.env.NODE_ENV === 'development') console.log('Handling Game Over Event...');
        setIsOpponentDisconnected(false);
        incrementGameCount();
        if (event.session) {
          setGameState(mapBackendSessionToState(event.session));
        } else if (event.winnerId || event.winningReason) {
          setGameState(prev => prev ? {
            ...prev, 
            winnerId: event.winnerId || prev.winnerId,
            winningReason: event.winningReason || prev.winningReason
          } : null);
        }

        setStatus('GAMEOVER');

        let finalMessage = 'Loja përfundoi.';
        if (event.winningReason === 'TIME_EXPIRED') {
          finalMessage = event.winnerId === playerId ? 'Urime, keni fituar! Kundërshtarit i mbaroi koha.' : 'Koha mbaroi. Ju keni humbur lojën.';
        } else if (event.winningReason === 'RESIGNED') {
          finalMessage = event.winnerId === playerId ? 'Fitore! Kundërshtari u tërhoq nga loja.' : 'U tërhoqët nga loja. Loja përfundoi.';
        } else if (event.winningReason === 'OPPONENT_ABANDONED') {
          finalMessage = 'Kundërshtari u largua nga loja.';
        }

        setMessage({
          text: event.message || finalMessage,
          type: event.winnerId === playerId ? 'success' : 'error'
        });
        break;
      }

      case 'ERROR': {
        const errorCode = event.code || 'default';
        gtag.event({ action: 'error_triggered', category: 'error', label: errorCode });
        let errorMessage = ERROR_MESSAGES[errorCode] || event.message || ERROR_MESSAGES['default'];

        if (errorCode === '404_0WNF' && lastAttemptedWord) {
          errorMessage = `Fjala "${lastAttemptedWord}" nuk ekziston në fjalorin tonë. Provoni një tjetër.`;
        } else if (errorCode === '400_0WAS' && lastAttemptedWord) {
          errorMessage = `Fjala "${lastAttemptedWord}" është përdorur tashmë.`;
        } else if (errorCode === '400_0IWS' && event.message) {
          const letterMatch = event.message.match(/letter\s(\w+)/i);
          if (letterMatch) {
            errorMessage = `Fjala duhet të fillojë me shkronjën "${letterMatch[1].toUpperCase()}".`;
          }
        }

        setMessage({text: errorMessage, type: 'error'});
        break;
      }
    }
  }, [mapBackendSessionToState, subscribeToGame, lastAttemptedWord, playerId]);

  handleGameEventRef.current = handleGameEvent;

  const restoreSession = useCallback(async (sId: string, pId: string) => {
    if (process.env.NODE_ENV === 'development') console.log(`Checking for session: ${sId}`);
    try {
      const res = await fetch(`${BACKEND_URL}/games/session/${sId}/restore`, {
        headers: {
          'Content-Type': 'application/json',
          'X-Player-ID': pId
        }
      });
      
      if (!res.ok) {
        localStorage.removeItem('fjalafundit_session_id');
        return;
      }

      const text = await res.text();
      const result = text ? JSON.parse(text) : null;

      if (result?.success && result.data) {
        const state = mapBackendSessionToState(result.data);
        setGameState(state);
        
        const me = state.player1.playerId === pId ? state.player1 : state.player2;
        const opp = state.player1.playerId === pId ? state.player2 : state.player1;

        if (me) {
          setPlayerName(me.name);
          localStorage.setItem('fjalafundit_last_name', me.name);
        }

        // Sync disconnection state from restored session
        if (opp?.connectionStatus === 'DISCONNECTED') {
          setIsOpponentDisconnected(true);
        } else {
          setIsOpponentDisconnected(false);
        }

        // Logic to determine status based on restored game state
        if (state.status === 'WAITING_FOR_READINESS') {
          setStatus(current => {
            if (skipCountdownRef.current || current === 'PLAYING' || current === 'COUNTDOWN') return current;
            return state.mode === 'SOLO_AI' ? 'PREPARING' : 'MATCHING';
          });
        } else if (state.status === 'WAITING_FOR_OPPONENT') {
          setStatus('WAITING');
        } else {
          skipCountdownRef.current = true;
          setStatus(current => (current === 'PLAYING' || current === 'COUNTDOWN') ? current : 'RECONNECTING');
        }

        subscribeToGame(sId);

        setTimeout(() => {
          if (stompClientRef.current?.connected) {
            stompClientRef.current.publish({
              destination: `/app/game/${sId}/reconnect`,
              body: JSON.stringify({ playerId: pId })
            });
          }
        }, 500);

        if (result.data.playedWords && result.data.playedWords.length > 0) {
          const sortedWords = [...result.data.playedWords].sort((a: PlayedWord, b: PlayedWord) => b.sortOrder - a.sortOrder);
          setHistory(sortedWords.map((pw: PlayedWord) => ({
            word: pw.word,
            playerId: pw.playerId, 
            score: pw.points,
            insight: pw.insight
          })));
        }
      } else {
        localStorage.removeItem('fjalafundit_session_id');
      }
    } catch (err) {
      console.error('Session check failed:', err);
      localStorage.removeItem('fjalafundit_session_id');
    }
  }, [mapBackendSessionToState, subscribeToGame]);

  useEffect(() => {
    let storedId = localStorage.getItem('fjalafundit_player_id');
    if (!storedId) {
      storedId = generateId();
      localStorage.setItem('fjalafundit_player_id', storedId);
    }
    setPlayerId(storedId);

    let retryCount = 0;
    const MAX_RETRIES = 3;

    let brokerUrl = WS_URL;
    if (typeof window !== 'undefined') {
      if (!brokerUrl.startsWith('ws://') && !brokerUrl.startsWith('wss://')) {
        const protocol = window.location.protocol === 'https:' ? 'wss' : 'ws';
        const host = window.location.host;
        if (brokerUrl.startsWith('http')) {
          brokerUrl = brokerUrl.replace(/^http/, 'ws');
        } else {
          // Handle relative path like '/api/ws'
          brokerUrl = `${protocol}://${host}${brokerUrl}`;
        }
      }
    }

    const client = new Client({
      brokerURL: brokerUrl,
      debug: process.env.NODE_ENV === 'development' ? (str) => console.log(str) : () => {},
      reconnectDelay: 5000,
      heartbeatIncoming: 4000,
      heartbeatOutgoing: 4000,
      connectHeaders: {
        playerId: storedId,
        sessionId: localStorage.getItem('fjalafundit_session_id') || ''
      }
    });

    client.onConnect = () => {
      retryCount = 0;
      setIsConnected(true);
      stompClientRef.current = client;

      const sId = localStorage.getItem('fjalafundit_session_id');
      if (sId && storedId) {
        restoreSession(sId, storedId);
      }

      client.subscribe(`/user/queue/match-found`, (msg: IMessage) => {
        const event = JSON.parse(msg.body);
        handleGameEventRef.current(event);
      });

      client.subscribe(`/user/queue/errors`, (msg: IMessage) => {
        handleGameEventRef.current(JSON.parse(msg.body));
      });
    };

    client.onDisconnect = () => {
      setIsConnected(false);
      stompClientRef.current = null;
    };

    client.onWebSocketError = () => {
      retryCount++;
      if (retryCount >= MAX_RETRIES) setIsSystemHealthy(false);
    };

    client.onStompError = () => {
      setIsSystemHealthy(false);
    };

    client.activate();

    return () => {
      client.deactivate();
    };
  }, [restoreSession]);

  const fetchWithId = useCallback(async (url: string, options: RequestInit = {}) => {
    return fetch(`${BACKEND_URL}${url}`, {
      ...options,
      headers: {
        ...options.headers,
        'Content-Type': 'application/json',
        'X-Player-ID': playerId
      }
    });
  }, [playerId]);

  const joinAiGame = async (name: string, autoReady: boolean = false) => {
    gtag.event({ action: 'mode_selected', category: 'game', label: 'SOLO_AI' });
    if (autoReady) {
      skipCountdownRef.current = true;
      setStatus('PLAYING');
      setHistory([]);
      setMessage(null);
    }
    setPlayerName(name);
    try {
      const res = await fetchWithId('/games/solo', {
        method: 'POST',
        body: JSON.stringify({ playerId, playerName: name })
      });
      const text = await res.text();
      const result = text ? JSON.parse(text) : { success: res.ok };
      
      if (result.success) {
        const state = mapBackendSessionToState(result.data as BackendSession);
        localStorage.setItem('fjalafundit_session_id', state.gameId);
        subscribeToGame(state.gameId);
        setGameState(state);
        setHistory([]);
        setMessage(null);
        
        if (autoReady) {
          skipCountdownRef.current = true;
          setStatus('PLAYING');
          setTimeout(() => {
            if (stompClientRef.current?.connected) {
              stompClientRef.current.publish({
                destination: `/app/game/${state.gameId}/ready`,
                body: JSON.stringify({ playerId })
              });
            }
          }, 150);
        } else {
          skipCountdownRef.current = false;
          setStatus('PREPARING');
        }
      } else {
        setMessage({ text: result.error?.message || 'Dështoi nisja e lojës.', type: 'error' });
        if (autoReady) setStatus('GAMEOVER');
      }
    } catch (err) {
      console.error('API error:', err);
      setIsSystemHealthy(false);
    }
  };

  const joinQueue = async (name: string) => {
    gtag.event({ action: 'mode_selected', category: 'game', label: 'PVP_MATCHMAKING' });
    setPlayerName(name);
    try {
      const res = await fetchWithId('/games/matchmaking/queue', {
        method: 'POST',
        body: JSON.stringify({ playerId, playerName: name })
      });
      
      if (!res.ok) {
        const errorText = await res.text();
        const errorData = errorText ? JSON.parse(errorText) : {};
        setMessage({ text: errorData.error?.message || 'Dështoi futja në radhë.', type: 'error' });
        return;
      }

      const text = await res.text();
      const result = text ? JSON.parse(text) : { success: true };

      if (result.success) {
        setGameState({ 
          mode: 'PVP_MATCHMAKING', 
          status: 'WAITING_FOR_OPPONENT',
          playedWords: []
        } as unknown as GameState);
        skipCountdownRef.current = false;
        setStatus('WAITING');
        setMessage({ text: 'Duke kërkuar kundërshtar...', type: 'info' });
      }
    } catch (err) {
      console.error('Matchmaking error:', err);
      setIsSystemHealthy(false);
    }
  };

  const leaveQueue = useCallback(async () => {
    try {
      await fetchWithId('/games/matchmaking/leave', {
        method: 'POST',
        body: JSON.stringify({ playerId })
      });
    } catch (err) {
      console.error('Failed to leave queue:', err);
    }
  }, [fetchWithId, playerId]);

  const createCustomGame = async (name: string, autoReady: boolean = false) => {
    gtag.event({ action: 'mode_selected', category: 'game', label: 'PVP_PRIVATE_ROOM' });
    if (autoReady) {
      skipCountdownRef.current = true;
      setStatus('PLAYING');
      setHistory([]);
      setMessage(null);
    }
    setPlayerName(name);
    try {
      const res = await fetchWithId('/games/private', {
        method: 'POST',
        body: JSON.stringify({ playerId, playerName: name })
      });
      const text = await res.text();
      const result = text ? JSON.parse(text) : { success: res.ok };

      if (result.success) {
        const state = mapBackendSessionToState(result.data as BackendSession);
        localStorage.setItem('fjalafundit_session_id', state.gameId);
        setGameState(state);
        setHistory([]);
        setMessage(null);
        
        subscribeToGame(state.gameId);
        
        if (autoReady) {
           skipCountdownRef.current = true;
           setStatus('PLAYING');
           setTimeout(() => {
             if (stompClientRef.current?.connected) {
               stompClientRef.current.publish({
                 destination: `/app/game/${state.gameId}/ready`,
                 body: JSON.stringify({ playerId })
               });
             }
           }, 150);
        } else {
           // Private room creators go to WAITING directly to see/share the code
           setStatus('WAITING');
        }
      } else {
        setMessage({ text: result.error?.message || 'Dështoi krijimi i dhomës.', type: 'error' });
        if (autoReady) setStatus('GAMEOVER');
      }
    } catch (err) {
      console.error('API error:', err);
      setIsSystemHealthy(false);
    }
  };

  const joinCustomGame = async (gameId: string, name: string, autoReady: boolean = false) => {
    setPlayerName(name);
    try {
      const res = await fetchWithId(`/games/private/${gameId}/join`, {
        method: 'POST',
        body: JSON.stringify({ playerId, playerName: name })
      });
      const text = await res.text();
      const result = text ? JSON.parse(text) : { success: res.ok };

      if (result.success) {
        const state = mapBackendSessionToState(result.data as BackendSession);
        localStorage.setItem('fjalafundit_session_id', state.gameId);
        subscribeToGame(state.gameId);
        setGameState(state);
        setHistory([]);
        setMessage(null);
        
        // When joining a private room, go to MATCHING to see the host
        setStatus('MATCHING');
        return result;
      } else {
        return result;
      }
    } catch (err) {
      console.error('API error:', err);
      setIsSystemHealthy(false);
      return { success: false, error: { message: 'Pati një problem gjatë lidhjes.' } };
    }
  };

  const cancelPrivateGame = async () => {
    if (!gameState?.gameId) return;
    try {
      await fetchWithId('/games/private/cancel', {
        method: 'POST',
        body: JSON.stringify({ hostId: playerId, sessionId: gameState.gameId })
      });
      // Always reset locally even if 404/400, but 204 is expected success
      resetGame();
    } catch (err) {
      console.error('Failed to cancel private game:', err);
      resetGame();
    }
  };

  const triggerReady = () => {
    if (gameState?.gameId) {
      stompClientRef.current?.publish({
        destination: `/app/game/${gameState.gameId}/ready`,
        body: JSON.stringify({ playerId })
      });
    }
  };

  const refuseMatch = () => {
    if (gameState?.gameId) {
      stompClientRef.current?.publish({
        destination: `/app/game/${gameState.gameId}/refuse`,
        body: JSON.stringify({ playerId })
      });
      
      if (gameState.mode === 'PVP_PRIVATE_ROOM') {
        resetGame();
      } else {
        // Immediately re-queue the person who refused for matchmaking
        const currentName = localStorage.getItem('fjalafundit_last_name') || playerName;
        joinQueue(currentName);
      }
    }
  };

  const requestRematch = () => {
    if (gameState?.gameId) {
      stompClientRef.current?.publish({
        destination: `/app/game/${gameState.gameId}/rematch`,
        body: JSON.stringify({ requesterId: playerId })
      });
    }
  };

  const acceptRematch = () => {
    if (gameState?.gameId) {
      // Swap roles on rematch
      const newHost = gameState.player2;
      const newGuest = gameState.player1;

      stompClientRef.current?.publish({
        destination: `/app/game/${gameState.gameId}/rematch/accept`,
        body: JSON.stringify({
          hostId: newHost?.playerId || '',
          hostName: newHost?.name || '',
          guestId: newGuest?.playerId || '',
          guestName: newGuest?.name || ''
        })
      });
      setRematchRequestBy(null);
    }
  };

  const declineRematch = () => {
    if (gameState?.gameId) {
      stompClientRef.current?.publish({
        destination: `/app/game/${gameState.gameId}/rematch/decline`,
        body: JSON.stringify({ playerId })
      });
      resetGame();
    }
  };

  const makeMove = (word: string) => {
    if (!gameState) return;
    setLastAttemptedWord(word);
    stompClientRef.current?.publish({
      destination: `/app/game/${gameState.gameId}/play`,
      body: JSON.stringify({ playerId, word })
    });
  };

  const leaveMatch = useCallback(() => {
    if (gameState?.gameId && status === 'GAMEOVER' && gameState.mode === 'PVP_PRIVATE_ROOM') {
      const oppId = gameState.player1.playerId === playerId ? gameState.player2?.playerId : gameState.player1.playerId;
      if (oppId && stompClientRef.current?.connected) {
        stompClientRef.current.publish({
          destination: `/app/game/${gameState.gameId}/leave`,
          body: JSON.stringify({ leavingPlayerId: playerId, opponentId: oppId })
        });
      }
    }
  }, [gameState, playerId, status]);

  useEffect(() => {
    const handleUnload = () => {
      leaveMatch();
    };
    window.addEventListener('beforeunload', handleUnload);
    return () => window.removeEventListener('beforeunload', handleUnload);
  }, [leaveMatch]);

  const resetGame = useCallback(() => {
    if (!gameState) {
      setStatus('LOBBY');
      return;
    }

    if (status === 'WAITING' && gameState.mode === 'PVP_MATCHMAKING') {
      leaveQueue();
    }
    
    if (status === 'PREPARING' || status === 'COUNTDOWN' || status === 'PLAYING') {
      if (stompClientRef.current?.connected) {
        stompClientRef.current.publish({
          destination: `/app/game/${gameState.gameId}/resign`,
          body: JSON.stringify({ playerId })
        });
      }
    }

    if (status === 'GAMEOVER' && gameState.mode === 'PVP_PRIVATE_ROOM') {
      leaveMatch();
    }

    localStorage.removeItem('fjalafundit_session_id');
    setStatus('LOBBY');
    setGameState(null);
    setHistory([]);
    setMessage(null);
        setRefusedByPlayerId(null);
        setIsOpponentDisconnected(false);
        setRematchRequestBy(null);
        if (gameSubscriptionRef.current) {
    
      gameSubscriptionRef.current.unsubscribe();
      gameSubscriptionRef.current = null;
    }
  }, [gameState, status, leaveQueue, playerId]);

  return (
    <GameContext.Provider value={{
      isConnected,
      status,
      gameState,
      history,
      playerName,
      playerId,
      isOpponentThinking,
      joinAiGame,
      joinQueue,
      createCustomGame,
      joinCustomGame,
      cancelPrivateGame,
      triggerReady,
      refuseMatch,
      requestRematch,
      acceptRematch,
      declineRematch,
      makeMove,
      resetGame,
      setStatus,
      message,
      setMessage,
      isSystemHealthy,
      serverTimeOffset,
      refusedByPlayerId,
      isOpponentDisconnected,
      rematchRequestBy,
      breakReminderEnabled,
      setBreakReminderEnabled,
      isBreakReminderVisible,
      dismissBreakReminder,
      customKeyboardEnabled,
      setCustomKeyboardEnabled,
      BACKEND_URL,
      fetchWithId,
      gameCount,
      incrementGameCount,
      isRatingModalVisible,
      setIsRatingModalVisible
    }}>
      {children}
    </GameContext.Provider>
  );
};